/**
 * Permissões (RBAC) da equipe da loja. Cada membro tem um papel (preset) e a
 * lista efetiva de permissões — editável por quem tem `users.manage`.
 * O proprietário (owner) sempre tem todas as permissões.
 */

export const PERMISSION_GROUPS = [
  {
    key: 'sales',
    label: 'Vendas',
    permissions: [
      { key: 'sales.create', label: 'Registrar vendas (PDV)', description: 'Acessar a tela Vender e finalizar vendas.' },
      { key: 'sales.view_all', label: 'Ver vendas de toda a equipe', description: 'Sem esta permissão, vê apenas as próprias vendas.' },
      { key: 'sales.cancel', label: 'Cancelar vendas', description: 'Cancelar vendas concluídas (devolve o estoque).' },
      { key: 'sales.discount', label: 'Dar descontos', description: 'Limitado pelo desconto máximo definido.' },
      { key: 'sales.change_price', label: 'Alterar preço no carrinho', description: 'Mudar o preço unitário durante a venda.' },
      { key: 'cash.operate', label: 'Operar o caixa', description: 'Abrir, fechar e registrar sangrias/suprimentos.' },
    ],
  },
  {
    key: 'orders',
    label: 'Pedidos',
    permissions: [
      { key: 'orders.manage', label: 'Gerenciar pedidos', description: 'Aceitar pedidos do catálogo e alterar status.' },
    ],
  },
  {
    key: 'products',
    label: 'Produtos e estoque',
    permissions: [
      { key: 'products.manage', label: 'Cadastrar e editar produtos', description: 'Inclui categorias e variações.' },
      { key: 'products.view_cost', label: 'Ver custo e lucro', description: 'Exibe preço de custo e margens.' },
      { key: 'stock.manage', label: 'Movimentar estoque', description: 'Entradas, saídas e ajustes de estoque.' },
      { key: 'purchases.manage', label: 'Compras e fornecedores', description: 'Registrar compras, importar NF-e e receber mercadoria.' },
      { key: 'inventory.manage', label: 'Inventário', description: 'Abrir contagens de estoque e aplicar os ajustes.' },
    ],
  },
  {
    key: 'customers',
    label: 'Clientes',
    permissions: [
      { key: 'customers.manage', label: 'Cadastrar e editar clientes', description: 'Inclui importar contatos.' },
      { key: 'customers.fiado', label: 'Fiado e recebimentos', description: 'Vender fiado e registrar pagamentos de clientes.' },
    ],
  },
  {
    key: 'store',
    label: 'Loja online',
    permissions: [
      { key: 'catalog.manage', label: 'Configurar catálogo online', description: 'Aparência, publicação e pedidos online.' },
      { key: 'coupons.manage', label: 'Gerenciar cupons', description: 'Criar e desativar cupons de desconto.' },
      { key: 'payments.manage', label: 'Pagamentos online', description: 'Conectar Mercado Pago ou Asaas e ver cobranças do site.' },
    ],
  },
  {
    key: 'finance',
    label: 'Finanças e relatórios',
    permissions: [
      { key: 'finance.view', label: 'Ver finanças', description: 'Fluxo de caixa, contas a pagar e a receber.' },
      { key: 'finance.manage', label: 'Lançar finanças', description: 'Criar, editar e baixar lançamentos.' },
      { key: 'reports.view', label: 'Ver estatísticas', description: 'Faturamento, ranking de clientes e vendedores.' },
      { key: 'intelligence.view', label: 'Ver inteligência', description: 'Recomendações, matriz de produtos e previsão de estoque.' },
      { key: 'data.export', label: 'Exportar relatórios', description: 'Baixar planilhas e PDFs.' },
    ],
  },
  {
    key: 'admin',
    label: 'Administração',
    permissions: [
      { key: 'users.manage', label: 'Gerenciar usuários', description: 'Convidar pessoas e definir permissões.' },
      { key: 'settings.manage', label: 'Alterar configurações', description: 'Dados da loja, recibo, pagamentos e entregas.' },
      { key: 'audit.view', label: 'Ver registro de atividades', description: 'Auditoria de tudo o que foi feito na loja.' },
      { key: 'billing.manage', label: 'Gerenciar assinatura', description: 'Plano, faturas e forma de pagamento.' },
    ],
  },
] as const;

export type PermissionKey = (typeof PERMISSION_GROUPS)[number]['permissions'][number]['key'];

export const ALL_PERMISSIONS: PermissionKey[] = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));

export type RoleKey = 'owner' | 'admin' | 'manager' | 'seller' | 'custom';

export const ROLE_LABELS: Record<RoleKey, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  manager: 'Gerente',
  seller: 'Vendedor',
  custom: 'Personalizado',
};

export const ROLE_DESCRIPTIONS: Record<RoleKey, string> = {
  owner: 'Acesso total, inclusive assinatura. Não pode ser removido.',
  admin: 'Acesso total à loja, exceto assinatura e pagamentos do plano.',
  manager: 'Gerencia vendas, produtos, estoque, clientes, catálogo e finanças.',
  seller: 'Vende no PDV, atende pedidos e cadastra clientes.',
  custom: 'Permissões escolhidas manualmente.',
};

export const ROLE_PRESETS: Record<Exclude<RoleKey, 'custom'>, PermissionKey[]> = {
  owner: ALL_PERMISSIONS,
  admin: ALL_PERMISSIONS.filter((p) => p !== 'billing.manage'),
  manager: [
    'sales.create', 'sales.view_all', 'sales.cancel', 'sales.discount', 'sales.change_price', 'cash.operate',
    'orders.manage', 'products.manage', 'products.view_cost', 'stock.manage', 'purchases.manage', 'inventory.manage', 'customers.manage',
    'customers.fiado', 'catalog.manage', 'coupons.manage', 'finance.view', 'finance.manage', 'reports.view', 'intelligence.view',
    'data.export',
  ],
  seller: ['sales.create', 'sales.discount', 'cash.operate', 'orders.manage', 'customers.manage', 'customers.fiado'],
};

export function permissionsForRole(role: RoleKey, custom?: readonly string[]): PermissionKey[] {
  if (role === 'custom') return (custom ?? []).filter((p): p is PermissionKey => ALL_PERMISSIONS.includes(p as PermissionKey));
  return [...ROLE_PRESETS[role]];
}

export function hasPermission(perms: readonly string[] | undefined, required: PermissionKey | PermissionKey[]): boolean {
  if (!perms) return false;
  const list = Array.isArray(required) ? required : [required];
  return list.every((p) => perms.includes(p));
}

export function hasAnyPermission(perms: readonly string[] | undefined, required: PermissionKey[]): boolean {
  if (!perms) return false;
  return required.some((p) => perms.includes(p));
}

// ----------------------------------------------------------------------------
// Superadmin
// ----------------------------------------------------------------------------

export type AdminRoleKey = 'owner' | 'admin' | 'support' | 'finance' | 'viewer';

export const ADMIN_ROLE_LABELS: Record<AdminRoleKey, string> = {
  owner: 'Dono da plataforma',
  admin: 'Administrador',
  support: 'Suporte',
  finance: 'Financeiro',
  viewer: 'Somente leitura',
};

export const ADMIN_PERMISSIONS = {
  'dashboard.view': ['owner', 'admin', 'support', 'finance', 'viewer'],
  'tenants.view': ['owner', 'admin', 'support', 'finance', 'viewer'],
  'tenants.manage': ['owner', 'admin'],
  'tenants.impersonate': ['owner', 'admin', 'support'],
  'tenants.notes': ['owner', 'admin', 'support', 'finance'],
  'users.manage': ['owner', 'admin', 'support'],
  'billing.view': ['owner', 'admin', 'finance', 'viewer'],
  'billing.manage': ['owner', 'admin', 'finance'],
  'plans.manage': ['owner', 'admin'],
  'audit.view': ['owner', 'admin', 'support', 'finance'],
  'admins.manage': ['owner'],
  'system.manage': ['owner', 'admin'],
  'feedback.manage': ['owner', 'admin', 'support'],
} as const satisfies Record<string, readonly AdminRoleKey[]>;

export type AdminPermissionKey = keyof typeof ADMIN_PERMISSIONS;

export function adminCan(role: AdminRoleKey | undefined, perm: AdminPermissionKey): boolean {
  if (!role) return false;
  return (ADMIN_PERMISSIONS[perm] as readonly AdminRoleKey[]).includes(role);
}
